import "@/index.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import { ThemeProvider } from "@/context/ThemeContext";
import { HelmetProvider } from "react-helmet-async";

// Pages
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import DashboardPage, { DashboardLayout } from "@/pages/DashboardPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import AdminChaptersPage from "@/pages/AdminChaptersPage";
import AdminCategoriesPage from "@/pages/AdminCategoriesPage";
import MembersPage from "@/pages/MembersPage";
import ReferralsPage from "@/pages/ReferralsPage";
import MeetingsPage from "@/pages/MeetingsPage";
import ChatPage from "@/pages/ChatPage";
import ProfilePage from "@/pages/ProfilePage";
import DigitalIdPage from "@/pages/DigitalIdPage";
import StorePage from "@/pages/StorePage";
import AIInsightsPage from "@/pages/AIInsightsPage";
import AdminVisitorsPage from "@/pages/AdminVisitorsPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import PublicProfilePage from "@/pages/PublicProfilePage";
import PartnerStoreDashboard from "@/pages/PartnerStoreDashboard";
import PartnerStoreSignup from "@/pages/PartnerStoreSignup";
import AdminPartnerStoresPage from "@/pages/AdminPartnerStoresPage";
import TestimonialsPage from "@/pages/TestimonialsPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AdminLeadsPage from "@/pages/AdminLeadsPage";
import PublicNav from "@/components/PublicNav";

const PUBLIC_PATHS = ["/", "/about", "/login", "/signup", "/partner-signup", "/forgot-password", "/reset-password"];

function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    if (PUBLIC_PATHS.includes(location.pathname) || location.pathname.startsWith("/p/")) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);
  return null;
}

const PublicLayout = () => {
  return (
    <>
      <PublicNav />
      <Outlet />
    </>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("bch_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error("Auth check failed:", error);
          localStorage.removeItem("bch_token");
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("bch_token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const signup = async (data) => {
    const response = await axios.post(`${API}/auth/signup`, data);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("bch_token");
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    signup,
    logout,
    isAuthenticated: !!token,
    isAdmin: user?.role === "super_admin" || user?.role === "president" || user?.role === "vice_president" || user?.role === "secretary",
    isSuperAdmin: user?.role === "super_admin",
    isPartnerStoreOnly: user?.role === "partner_store",
    hasPartnerStore: user?.has_partner_store === true
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, loading, isSuperAdmin, isPartnerStoreOnly } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-main flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-bch-teal border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Partner store only users get redirected to their dashboard
  if (isPartnerStoreOnly) {
    return <Navigate to="/partner-dashboard" replace />;
  }
  
  if (adminOnly && !isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <HelmetProvider>
    <ThemeProvider>
      <div className="noise-bg min-h-screen">
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              {/* Public Routes with shared nav */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
              </Route>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/p/:userId" element={<PublicProfilePage />} />
              <Route path="/partner-signup" element={<PartnerStoreSignup />} />
              
              {/* Protected Routes with persistent DashboardLayout */}
              <Route element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/members" element={<MembersPage />} />
                <Route path="/referrals" element={<ReferralsPage />} />
                <Route path="/meetings" element={<MeetingsPage />} />
                <Route path="/chat" element={<ChatPage />} />
                <Route path="/profile/:userId?" element={<ProfilePage />} />
                <Route path="/id-card" element={<DigitalIdPage />} />
                <Route path="/stores" element={<StorePage />} />
                <Route path="/ai-insights" element={<AIInsightsPage />} />
                <Route path="/leaderboard" element={<LeaderboardPage />} />
                <Route path="/testimonials" element={<TestimonialsPage />} />
                <Route path="/admin/users" element={
                  <ProtectedRoute adminOnly><AdminUsersPage /></ProtectedRoute>
                } />
                <Route path="/admin/chapters" element={
                  <ProtectedRoute adminOnly><AdminChaptersPage /></ProtectedRoute>
                } />
                <Route path="/admin/categories" element={
                  <ProtectedRoute adminOnly><AdminCategoriesPage /></ProtectedRoute>
                } />
                <Route path="/admin/visitors" element={
                  <ProtectedRoute adminOnly><AdminVisitorsPage /></ProtectedRoute>
                } />
                <Route path="/admin/partner-stores" element={
                  <ProtectedRoute adminOnly><AdminPartnerStoresPage /></ProtectedRoute>
                } />
                <Route path="/admin/leads" element={
                  <ProtectedRoute adminOnly><AdminLeadsPage /></ProtectedRoute>
                } />
                <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
              </Route>
              <Route path="/partner-dashboard" element={
                (() => {
                  const PartnerRoute = () => {
                    const { isAuthenticated, loading } = useAuth();
                    if (loading) return <div className="min-h-screen bg-main flex items-center justify-center"><div className="w-8 h-8 border-2 border-bch-teal border-t-transparent rounded-full animate-spin" /></div>;
                    if (!isAuthenticated) return <Navigate to="/login" replace />;
                    return <PartnerStoreDashboard />;
                  };
                  return <PartnerRoute />;
                })()
              } />
              <Route path="/admin/partner-stores" element={
                <ProtectedRoute adminOnly>
                  <AdminPartnerStoresPage />
                </ProtectedRoute>
              } />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </div>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
